import React, { useState } from 'react'
import { useTranslation } from 'i18n'

import { CSSTransition } from 'react-transition-group'

import analytics from 'global/AnalyticsData'
import ShareIcons from 'components/ShareIcons'
import { scrollToTopOfCurrentStep } from 'services/break'
import { sleep } from 'util/misc'

import ChecklistPanel from './components/ChecklistPanel'
import ResultPanel from './components/ResultListPanel'
import ResultCardPanel from './components/ResultCardPanel'

import './GroceryStep.scss'

const GroceryStep = ({
  brk,
  step,
  onEngage = function () {},
  onAwardPoints = function () {},
  nextStepButton,
  skipButton,
}) => {
  const { t } = useTranslation('grocery')
  const grocery = step && step.grocery

  const [checklistResult, setChecklistResult] = useState(false)
  const [checkedItemsList, setCheckedItemsList] = useState([])
  const animationDuration = 300

  const handleResultCardClick = async (id, url) => {
    analytics.stepEvent('step_card_click', step, {
      context_id: id,
      context: url,
    })
  }

  const handleResultLinkClick = async (id, text, url) => {
    analytics.stepEvent('step_button_click', step, {
      result_id: id,
      result: text + ' | ' + url,
    })
  }

  const handleShareClick = async ({ platform }) => {
    analytics.stepEvent('step_share_click', step, {
      context_id: platform,
      context: platform,
    })
  }

  const handleClipboardCopy = async () => {
    analytics.stepEvent('step_clipboard_copy_click', step)
  }

  const handleSubmit = async () => {
    scrollToTopOfCurrentStep(step.id)
    await sleep(animationDuration)

    onEngage()
    onAwardPoints()

    let resultItems = []
    if (grocery.resultInclude === 'SELECTED') {
      // loop through each checklist and grab the items for each checked item
      grocery.groceryChecklists.forEach((groceryChecklist, i) => {
        // using .filter might be more clear than .reduce
        resultItems.push(
          ...groceryChecklist.items.reduce((previous, item, i) => {
            if (checkedItemsList.includes(item.id)) {
              previous.push(item)
            }
            return previous
          }, []),
        )
      }, [])
    } else {
      // opposite. grab items not checked.
      grocery.groceryChecklists.forEach((groceryChecklist, i) => {
        resultItems.push(
          ...groceryChecklist.items.reduce((previous, item, i) => {
            if (!checkedItemsList.includes(item.id)) {
              // NOT is here
              previous.push(item)
            }
            return previous
          }, []),
        )
      }, [])
    }

    if (grocery.resultOrderType === 'ALPHABETICAL') {
      resultItems = resultItems.sort((a, b) => {
        if (a['label'] < b['label']) {
          return -1
        } else if (a['label'] > b['label']) {
          return 1
        } else {
          return 0
        }
      })
    }
    // console.log(resultItems)
    setChecklistResult(resultItems)
  } // handleSubmit

  const handleClickCheckbox = (checked, id, label) => {
    if (checked) {
      checkedItemsList.push(id)
    } else {
      const index = checkedItemsList.indexOf(id)
      if (index > -1) {
        checkedItemsList.splice(index, 1)
      }
    }
    setCheckedItemsList(checkedItemsList)
  }

  const socialShareComponent = step.socialShareOn && (
    <ShareIcons
      facebookShare={step.facebookShare}
      twitterShare={step.twitterShare}
      pinterestShare={step.pinterestShare}
      emailShare={step.emailShare}
      emailConfig={{
        subject: step.shareEmailSubject,
        body: step.shareEmailBody,
      }}
      copyShare={step.copyShare}
      whatsappShare={step.whatsappShare}
      shareUrl={brk ? brk.shareUrl : ''}
      onShareClick={handleShareClick}
    />
  )

  return (
    <section className="grocery-step3">
      <div
        className={`step-content ${
          brk?.navigationType === 'TOP' ? 'top-navigation' : ''
        } ${step.backgroundImage ? ' with-shadow' : ''}`}
      >
        <CSSTransition
          in={!checklistResult}
          timeout={animationDuration}
          classNames="exit-fadeout"
          unmountOnExit={true}
          onExit={() => {
            // with long lists we need to make sure we get back to the top
            scrollToTopOfCurrentStep(step.id)
          }}
          onExiting={() => {
            scrollToTopOfCurrentStep(step.id)
          }}
          onExited={() => {
            scrollToTopOfCurrentStep(step.id)
          }}
        >
          <div>
            {' '}
            {/* need a wrapper div for CSSTransition classes */}
            <div className="step-intro step-subcopy">
              {grocery.title && (
                <h2
                  className="step-content__title"
                  dangerouslySetInnerHTML={{ __html: grocery.title }}
                />
              )}
              <div
                className="step-content__intro"
                dangerouslySetInnerHTML={{ __html: grocery.copy }}
              />
            </div>
            {grocery &&
              grocery.groceryChecklists &&
              grocery.groceryChecklists.map((groceryChecklist, i) => {
                const { id, title, copy, imageType, image, items } =
                  groceryChecklist

                return (
                  <ChecklistPanel
                    key={i}
                    id={id}
                    title={title}
                    detail={copy}
                    imageType={imageType}
                    imageUrl={
                      image ? `${process.env.SITE_ASSET_URL}/${image.id}` : null
                    }
                    imageAlt={image ? image.altAttribute : null}
                    items={items}
                    disabled={false}
                    onClick={handleClickCheckbox}
                  />
                )
              })}
            <div className="submit-container">
              <input
                type="submit"
                className="btn themed-button"
                disabled={checklistResult}
                value={t('common:submit')}
                onClick={handleSubmit}
              />
            </div>
          </div>
        </CSSTransition>

        <CSSTransition
          in={!!checklistResult}
          timeout={animationDuration}
          classNames="enter-fadein"
        >
          {checklistResult ? (
            grocery.resultDisplayFormat === 'CARDS' ? (
              <ResultCardPanel
                title={grocery.resultTitle}
                detail={grocery.resultCopy}
                imageUrl={
                  grocery.resultImage
                    ? `${process.env.SITE_ASSET_URL}/${grocery.resultImage.id}`
                    : null
                }
                imageAlt={
                  grocery.resultImage ? grocery.resultImage.altAttribute : null
                }
                items={checklistResult}
                onCardClick={handleResultCardClick}
                dropdownButtons={grocery.resultDropdownButtons}
                onLinkClick={handleResultLinkClick}
                shareIcons={socialShareComponent}
                onClipboardCopy={handleClipboardCopy}
              />
            ) : (
              <ResultPanel
                title={grocery.resultTitle}
                detail={grocery.resultCopy}
                imageUrl={
                  grocery.resultImage
                    ? `${process.env.SITE_ASSET_URL}/${grocery.resultImage.id}`
                    : null
                }
                imageAlt={
                  grocery.resultImage ? grocery.resultImage.altAttribute : null
                }
                items={checklistResult}
                dropdownButtons={grocery.resultDropdownButtons}
                onLinkClick={handleResultLinkClick}
                shareIcons={socialShareComponent}
                onClipboardCopy={handleClipboardCopy}
              />
            )
          ) : (
            <></>
          )}
        </CSSTransition>
        {skipButton}
        {nextStepButton}
      </div>
    </section>
  )
}

export default GroceryStep
